import clsx from "clsx";

type Column<T> = {
  header: string;
  accessor?: keyof T;
  cell?: (row: T, index: number) => React.ReactNode;
  className?: string;
};

type DataTableProps<T> = {
  data: T[];
  columns: Column<T>[];
  noDataMessage?: string;
  maxHeight?: string;
};

const rowCornerClassName = (isFirst: boolean, isLast: boolean, isSingle: boolean, side: "left" | "right") => {
  const [top, bottom] = side === "left" ? ["rounded-tl-2xl", "rounded-bl-2xl"] : ["rounded-tr-2xl", "rounded-br-2xl"];
  return clsx(isSingle && [top, bottom], isFirst && !isSingle && top, isLast && !isSingle && bottom);
};

const DataTable = <T,>({ data, columns, noDataMessage = "No results found", maxHeight = "400px" }: DataTableProps<T>) => {
  return (
    <div className="mt-5 pb-5 w-full">
      <div className="bg-white rounded-lg">
        <div className="scroll-inner mt-3 rounded-2xl overflow-y-auto" style={{ maxHeight }}>
          <table className="w-full border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
            <thead className="bg-[#F5F5F5] text-[#1F1F23] text-xs font-normal sticky top-0 z-10">
              <tr>
                {columns.map((col, idx) => (
                  <th
                    key={idx}
                    scope="col"
                    className={clsx(
                      "p-2 py-3 whitespace-nowrap text-left text-xs font-medium text-black capitalize tracking-wider bg-[#F5F5F5]",
                      col.className,
                    )}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-xs">
              {data.length > 0 ? (
                data.map((row, rowIndex) => {
                  const isFirst = rowIndex === 0;
                  const isLast = rowIndex === data.length - 1;
                  const isSingle = data.length === 1;

                  return (
                    <tr
                      key={rowIndex}
                      className={clsx(
                        "group bg-white hover:bg-gray-50",
                        isSingle && "rounded-xl",
                        isFirst && !isSingle && "rounded-t-xl",
                        isLast && !isSingle && "rounded-b-xl",
                      )}
                    >
                      {columns.map((col, colIndex) => (
                        <td
                          key={colIndex}
                          className={clsx(
                            "p-2 whitespace-nowrap text-xs font-normal",
                            colIndex === 0 && rowCornerClassName(isFirst, isLast, isSingle, "left"),
                            colIndex === columns.length - 1 && rowCornerClassName(isFirst, isLast, isSingle, "right"),
                            col.className,
                          )}
                        >
                          {col.cell
                            ? col.cell(row, rowIndex)
                            : col.accessor
                              ? (row[col.accessor] as React.ReactNode)
                              : null}
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr className="bg-white rounded-xl">
                  <td colSpan={columns.length} className="p-2 py-4 text-center text-gray-500 text-xs rounded-xl">
                    {noDataMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DataTable;
